import { LinkHTMLAttributes, useEffect, useState } from "react";
import { Product } from "~/entities/product/types";
import { productNameFrom } from "~/entities/product/utils";
import { useHydrated } from "~/hooks/useHydrated";

const Item: React.FC<
  React.PropsWithChildren<LinkHTMLAttributes<HTMLElement>>
> = ({ children, className, ...rest }) => (
  <li
    className={`after:mx-[0.5vw] after:inline-block ${className}`}
    {...rest}
  >
    {children}
  </li>
);

const Link: React.FC<
  React.PropsWithChildren<LinkHTMLAttributes<HTMLElement>>
> = ({ children, className, ...rest }) => (
  <a
    className={`text-blue400 visited:text-blue450 ${className}`}
    {...rest}
  >
    {children}
  </a>
);

type Props = {
  product: Product;
  referer?: string;
};

const ProductBreadcrumbs: React.FC<Props> = ({
  referer: refererFromProps,
  product,
}) => {
  const isHydrated = useHydrated();
  const { mill, category } = product;
  const [referrer, setReferrer] = useState<string | undefined>(
    refererFromProps,
  );

  useEffect(() => {
    if (!isHydrated) return;

    try {
      const referrerURL = new URL(document.referrer);

      if (referrerURL.hostname !== window.location.hostname) {
        setReferrer(refererFromProps);
        return;
      }

      if (referrerURL.pathname === location.pathname) {
        setReferrer(refererFromProps);
        return;
      }

      setReferrer(document.referrer);
      return;
    } catch (error) {
      setReferrer(refererFromProps);
    }
  }, [isHydrated, refererFromProps]);

  return (
    <nav
      className="w-full"
      aria-label="Breadcrumb"
    >
      <ol className="my-3.5 flex w-full overflow-hidden overflow-x-scroll whitespace-nowrap text-sm md:overflow-visible md:overflow-x-auto">
        {referrer ? (
          <Item className="after:content-['|']">
            <Link href={referrer}>Back to Results</Link>
          </Item>
        ) : null}
        {mill ? (
          <Item className="after:content-['>']">
            <Link href={`/${mill.slug}`}>{mill.name}</Link>
          </Item>
        ) : null}
        {mill && category ? (
          <Item className="after:content-['>']">
            <Link href={`/${mill.slug}/${category.slug}`}>{category.name}</Link>
          </Item>
        ) : null}
        <Item
          aria-current="page"
          className="text-black"
          dangerouslySetInnerHTML={{ __html: productNameFrom(product) }}
        />
      </ol>
    </nav>
  );
};

export default ProductBreadcrumbs;
